/**
 * Test-Records scenario package contract (scenario.json) v1.0.
 *
 * Dependency-free TypeScript mirror of the C# DTOs in
 * `src/PipeEX.TestRecords.Contracts` and the JSON Schema at
 * `contracts/test-records/scenario.schema.json` (the single source of truth).
 *
 * Pure types + frozen constants only — no runtime dependencies.
 */

/** Current contract version, MAJOR.MINOR. See contracts/test-records/README.md. */
export const SCENARIO_SCHEMA_VERSION = '1.0' as const;

/**
 * Maximum inline payload size, in UTF-8 bytes. Payloads larger than this MUST be
 * externalized to a folder and referenced via a `*Ref` field instead of inlined.
 */
export const EXTERNALIZATION_THRESHOLD_BYTES = 16384 as const;

/** The kind of a recorded step. */
export type ScenarioStepType = 'ui' | 'grpc' | 'revit';

/** Identity and provenance of the recorded scenario. */
export interface ScenarioInfo {
  /** Stable identifier (uuid recommended). */
  id: string;
  name: string;
  /** ISO-8601 UTC timestamp. */
  createdAt: string;
  /** Login or display name of the recording operator. */
  createdBy: string;
  description?: string;
}

/** Versions of every tier involved when the scenario was recorded. */
export interface EnvironmentInfo {
  appVersion: string;
  webClientVersion: string;
  revitPluginVersion: string;
  revitVersion: string;
  os: string;
}

/** A single ordered step in the recorded scenario. */
export interface ScenarioStep {
  /** Zero-based position in the ordered step sequence. */
  index: number;
  type: ScenarioStepType;
  action: string;
  target?: string;
  /** ISO-8601 UTC timestamp of the step. */
  timestamp?: string;
  /**
   * Free-form, step-specific data. Large blobs must be externalized via a
   * `*Ref` path instead of inlined here — see the externalization rule.
   */
  details?: Record<string, unknown>;
  /**
   * Relative path (under `screenshots/`) to the PNG captured at this step.
   * Present only for key steps in a Full-mode recording — see card G. Additive
   * optional field (backward-compatible within schemaVersion 1).
   */
  screenshotRef?: string;
}

/** The opened Revit document. */
export interface RevitDocumentInfo {
  title: string;
  /** Relative path (under model/) to the captured .rvt, when bundled. */
  modelPathRef?: string;
}

/** Revit-side context captured at scenario start. */
export interface RevitContext {
  document: RevitDocumentInfo;
  /** Revit UniqueIds selected at scenario start. */
  selection?: string[];
}

/** A Revit element referenced by the scenario, keyed by UniqueId. */
export interface ScenarioElement {
  /** Revit UniqueId — the primary, stable element key. */
  uniqueId: string;
  /** Revit ElementId integer. Session-local; never the primary key. */
  elementId?: number;
  category?: string;
  /** Relative path (under elements/) to this element's externalized parameter set. */
  parametersRef?: string;
}

/** A recorded gRPC call between the web client and the Revit runtime. */
export interface GrpcCall {
  /** Zero-based position in the recorded gRPC call sequence. */
  index: number;
  /** Fully-qualified gRPC method, e.g. pipeex.PcfService/CollectSnapshotForExport. */
  method: string;
  /** Relative path (under grpc/) to the externalized request payload. */
  requestRef?: string;
  /** Relative path (under grpc/) to the externalized response payload. */
  responseRef?: string;
  status?: string;
  durationMs?: number;
}

/** An expectation the replay engine checks after running the scenario. */
export interface ScenarioAssertion {
  id: string;
  /** Assertion family, e.g. fileExists, pcfRunRow, grpcStatus. */
  kind: string;
  target?: string;
  /** Expected value. Any JSON type. */
  expected?: unknown;
  description?: string;
}

/**
 * Relative refs to the recorder diagnostics logs bundled under `logs/`. Each is
 * present only when that log has content. Additive optional field
 * (backward-compatible within schemaVersion 1).
 */
export interface ScenarioLogs {
  /** Structured recorder lifecycle/diagnostics log (`logs/recorder.log`). */
  recorderLog?: string;
  /** Per-call gRPC summary: method/status/duration/correlationId (`logs/grpc.log`). */
  grpcLog?: string;
  /** Errors + warnings only, aggregated across recorder + gRPC + plugin (`logs/errors.log`). */
  errorsLog?: string;
}

/**
 * Privacy/redaction settings that were applied when the archive was built.
 * Recorded so a scenario is reproducible (the consumer knows what was masked or
 * excluded). Additive optional field (backward-compatible within schemaVersion 1).
 */
export interface ScenarioPrivacy {
  /** Absolute host paths were stripped from all files. */
  excludeAbsolutePaths?: boolean;
  /** Directory parts of absolute paths were masked (file names kept). */
  maskLocalDirectories?: boolean;
  /** The recording user's name was masked everywhere. */
  maskUsername?: boolean;
  /** The model copy was excluded from the archive. */
  modelExcluded?: boolean;
  /** Large gRPC/element payloads were excluded (stub refs kept). */
  bigPayloadsExcluded?: boolean;
  /** Capture detail level in effect (mirrors Full/Lightweight). */
  detailLevel?: 'full' | 'lightweight';
}

/**
 * Root manifest (scenario.json) of a recorded scenario package.
 * Mirrors contracts/test-records/scenario.schema.json v1.0.
 */
export interface ScenarioPackage {
  /** Contract version, MAJOR.MINOR (e.g. "1.0"). */
  schemaVersion: string;
  scenario: ScenarioInfo;
  environment: EnvironmentInfo;
  /** Ordered UI / gRPC / Revit steps captured during the recording. */
  steps: ScenarioStep[];
  revit?: RevitContext;
  elements?: ScenarioElement[];
  grpc?: GrpcCall[];
  assertions?: ScenarioAssertion[];
  /**
   * Human-readable notes about data that could not be collected — gaps made
   * explicit. Mirrors the WARN lines in logs/recorder.log. Additive optional
   * field (backward-compatible within schemaVersion 1).
   */
  warnings?: string[];
  /** Relative refs to the bundled diagnostics logs (`logs/`). */
  logs?: ScenarioLogs;
  /** Privacy/redaction settings applied at build time (for reproducibility). */
  privacy?: ScenarioPrivacy;
}
